import { Game, User } from '@olagg/db-types';
import { UserEndpoints } from '../endpoints/endpoints';
import { useState } from 'react';
import { tabs } from '../../../apps/app/src/modules/Ranking/constants';
import { fetcher } from '../fetcher';
import { useTranslation } from "react-i18next";

enum ParentType {
  QUEST = "QUEST",
  TOURNAMENT = "TOURNAMENT",
  USER_CONTENT = "USER_CONTENT",
  OTHER = "OTHER",
  GAME = "GAME",
};

export { ParentType };

export type RankingFilter = {
  limit?: number,
  from?: string,
  to?: string,
  slug?: string,
  currentTabIndex?: number,
  labelMobile?: any,
  label?: string,
  organizerId?: string,
}

export type Position = {
  user: {
    id: number,
    name: string,
    avatar: string
  },
  participated: number,
  won: number,
  participatingStreak: number,
  winningStreak: number,
  score: number,
  prize: number
}

export type MyPosition = {
  position: number,
  user: {
    id: number,
    name: string,
    avatar: string,
    score: number
  }
}

export enum ScorableType {
  TOURNAMENTS = 'tournament',
  QUESTS = 'quests',
  USER_CONTENT = 'user_content',
  GENERAL = ''
}

export type setFilterType = (newFilter: RankingFilter) => void;

export type useRankingType = ReturnType<typeof useRanking>;

export const useRanking = (defaultFilter?: RankingFilter) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<RankingFilter>(defaultFilter || {});
  const [ranking, setRanking] = useState<Position[]>([]);
  const [myRanking, setMyRanking] = useState<MyPosition | null>(null);
  const [gamesList, setGamesList] = useState<Game[]>([]);
  const [tabIndex, setTabIndex] = useState<number>(defaultFilter?.currentTabIndex || 0);
  const [gameOptions, setGameOptions] = useState([{ label: t('Ranking.gameOptions.allGames'), slug: null }]);
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalItems, setTotalItems] = useState<number>(0)

  const onSuccess = (response: { ranking: Position[], meta: { count: number } }) => {
    setTotalItems(response.meta.count)
    setRanking(response.ranking);
  }

  const onError = () => {
    setRanking([])
    setTotalItems(0)
  }

  const getGeneralRanking = () => {
    setIsLoading(true)
    fetcher(UserEndpoints.generalRanking(filter.limit, filter.from, filter.to, filter.slug))
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));
  }

  const getTournamentsRanking = () => {
    setIsLoading(true)
    fetcher(UserEndpoints.tournamentRanking(filter.limit, filter.from, filter.to, filter.slug, filter.organizerId))
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));
  }

  const getQuestsRanking = () => {
    setIsLoading(true)
    fetcher(UserEndpoints.questRanking(filter.limit, filter.from, filter.to, filter.slug))
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));
  }

  const getUserContentRanking = () => {
    setIsLoading(true)
    fetcher(UserEndpoints.userContentRanking(filter.limit, filter.from, filter.to, filter.slug))
      .then(onSuccess)
      .catch(onError)
      .finally(() => setIsLoading(false));
  }

  const getMyRanking = () =>
    fetcher(UserEndpoints.myRank(filter.from, filter.to, filter.slug))
      .then((response) => {
        console.log(response)
        setMyRanking(response)
      })
      .catch(() => setMyRanking(null))


  const getRanking = () => {
    getMyRanking();
    switch (tabIndex) {
      case tabs['General'].index:
        getGeneralRanking();
        break;
      case tabs['Tournaments'].index:
        getTournamentsRanking();
        break;
      case tabs['Quests'].index:
        getQuestsRanking();
        break;
      case tabs['UserContent'].index:
        getUserContentRanking();
        break;
      default:
        break;
    }
  }

  const getGames = (scorable_type?: ScorableType) => {
    setIsLoading(true)
    fetcher(UserEndpoints.gamesWithScore(scorable_type))
      .then((response) => {
        const gameOptions = [
          { label: t('Ranking.gameOptions.allGames'), slug: null },
          ...response.map((game) => ({ label: game[1], value: game[0] }))
        ];
        setGameOptions(gameOptions)
      })
      .catch(() => {
        setGameOptions([{ label: t('Ranking.gameOptions.allGames'), slug: null }])
      })
      .finally(() => setIsLoading(false))
  }

  return {
    tabs,
    tabIndex,
    setTabIndex,
    currentFilter: filter,
    ranking,
    myRanking,
    filter,
    setFilter,
    getGeneralRanking,
    getTournamentsRanking,
    getQuestsRanking,
    getUserContentRanking,
    getRanking,
    getGames,
    gamesList,
    gameOptions,
    isLoading,
    totalItems,
    setTotalItems
  }
}

